import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Button, Chip, Dialog } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { unwrapResult } from "@reduxjs/toolkit";
import { Localized, useLocalization } from "@fluent/react";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { useAppDispatch } from "../../../app/hooks";
import { showError } from "../../notifications/notificationsSlice";
import { DocumentSectionMapping, loadDocumentSectionMappings } from "./documentSectionMappingsApi";
import { DocumentSectionMappingsTransferDialog } from "./dialogs/DocumentSectionMappingsTransferDialog";
import { DOCUMENT_TYPES, DocumentTypes } from "../../documents/documentTypes";

type DialogState =
	| { type: "none" }
	| { type: "transfer"; documentType: DocumentTypes };

const DialogContentDispatcher = ({
	state,
	setter,
	mappings,
	onSuccess,
}: {
	state: DialogState;
	setter: Dispatch<SetStateAction<DialogState>>;
	mappings: DocumentSectionMapping[];
	onSuccess: (mappings: DocumentSectionMapping[]) => void;
}) => {
	const onCancel = useCallback(() => {
		setter({ type: "none" });
	}, [setter]);

	switch (state.type) {
		case "none":
			return null;
		case "transfer":
			return (
				<DocumentSectionMappingsTransferDialog
					documentType={state.documentType}
					mappings={mappings}
					onCancel={onCancel}
					onSuccess={onSuccess}
				/>
			);
	}
};

type DocumentTypeRow = {
	id: string;
	documentType: DocumentTypes;
	mappingCount: number;
	isMissing: boolean;
	order: number;
};

export const DocumentSectionMappings = () => {
	const { l10n } = useLocalization();
	const dispatch = useAppDispatch();

	const [mappings, setMappings] = useState<DocumentSectionMapping[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [dialogState, setDialogState] = useState<DialogState>({ type: "none" });

	const reloadMappings = useCallback(async () => {
		setIsLoading(true);
		try {
			const result = await dispatch(loadDocumentSectionMappings());
			setMappings(unwrapResult(result));
		} catch {
			dispatch(showError("error"));
		} finally {
			setIsLoading(false);
		}
	}, [dispatch]);

	useEffect(() => {
		reloadMappings();
	}, [reloadMappings]);

	const handleTransferSave = useCallback((updatedMappings: DocumentSectionMapping[]) => {
		setDialogState({ type: "none" });

		const documentType = updatedMappings[0]?.documentType;
		if (!documentType) {
			return;
		}

		setMappings((previous) => [
			...previous.filter((item) => item.documentType !== documentType),
			...updatedMappings,
		]);
	}, []);

	const renderMissingMappingCell = (isMissing: boolean) => {
		if (!isMissing) {
			return <></>;
		}

		return (
			<Chip
				size="small"
				color="error"
				icon={<ErrorOutlineIcon />}
				label={l10n.getString("document-section-mappings-missing", null, "Brak mapowania")}
			/>
		);
	};

	const documentTypeRows = useMemo<DocumentTypeRow[]>(() => {
		return DOCUMENT_TYPES.map((documentType, index) => {
			const mappingCount = mappings.filter((item) => item.documentType === documentType).length;

			return {
				id: documentType,
				documentType,
				mappingCount,
				isMissing: mappingCount === 0,
				order: index,
			};
		}).sort((left, right) => {
			if (left.isMissing !== right.isMissing) {
				return Number(left.isMissing) - Number(right.isMissing);
			}

			return left.order - right.order;
		});
	}, [mappings]);

	const documentTypeColumns: GridColDef<DocumentTypeRow>[] = [
		{
			field: "documentType",
			headerName: l10n.getString("document-type", null, "_Typ dokumentu"),
			flex: 1,
			minWidth: 200
		},
		{
			field: "mappingStatus",
			headerName: "",
			minWidth: 190,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params) => renderMissingMappingCell(params.row.isMissing),
		},
		{
			field: "actions",
			headerName: "",
			type: "actions",
			width: 170,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params) => (
				<Button
					size="small"
					color="secondary"
					variant="outlined"
					onClick={() => setDialogState({ type: "transfer", documentType: params.row.documentType })}
				>
					<Localized id="details">
						<span>_Szczegóły</span>
					</Localized>
				</Button>
			),
		},
	];

	if (isLoading) {
		return <LoadingContainer />;
	}

	return (
		<>
			<DataGrid
				rows={documentTypeRows}
				columns={documentTypeColumns}
				getRowId={(row) => row.id}
				disableRowSelectionOnClick
			/>

			<Dialog
				open={dialogState.type === "transfer"}
				fullWidth
				maxWidth="xl"
				keepMounted={false}
				PaperProps={{ sx: { height: "90vh" } }}
			>
				<DialogContentDispatcher
					state={dialogState}
					setter={setDialogState}
					mappings={mappings}
					onSuccess={handleTransferSave}
				/>
			</Dialog>
		</>
	);
};
